import React, { useState, useEffect } from 'react';

interface MaintenanceModePageProps {
  message?: string;
  endsAt?: string;
  autoRefresh?: boolean;
}

export const MaintenanceModePage: React.FC<MaintenanceModePageProps> = ({
  message = 'System maintenance in progress. We will be back shortly.',
  endsAt,
  autoRefresh = true
}) => {
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!endsAt) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const end = new Date(endsAt).getTime();
      const distance = end - now;

      if (distance < 0) {
        setIsExpired(true);
        setTimeRemaining('Maintenance completed');
        
        // Auto-refresh after 5 seconds if maintenance ended
        if (autoRefresh) {
          setTimeout(() => {
            window.location.reload();
          }, 5000);
        }
        return;
      }

      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [endsAt, autoRefresh]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Maintenance Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-yellow-500/20 rounded-full mb-6">
            <svg 
              className="w-12 h-12 text-yellow-500 animate-pulse" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
              />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            🔧 Under Maintenance
          </h1>
        </div>

        {/* Maintenance Message Card */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-8 shadow-2xl">
          <div className="text-center">
            <p className="text-gray-300 text-lg mb-6 leading-relaxed">
              {message}
            </p>

            {endsAt && !isExpired && (
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6 mb-6">
                <p className="text-white text-sm mb-2">Estimated completion time:</p>
                <p className="text-3xl font-bold text-yellow-500 mb-2">
                  {timeRemaining}
                </p>
                <p className="text-gray-500 text-sm">
                  Expected to complete at {new Date(endsAt).toLocaleTimeString()}
                </p>
              </div>
            )}

            {isExpired && (
              <div className="bg-green-900/20 border border-green-700 rounded-lg p-6 mb-6">
                <p className="text-green-500 text-xl font-semibold mb-2">
                  ✅ Maintenance Completed!
                </p>
                <p className="text-white text-sm">
                  Refreshing page in 5 seconds...
                </p>
              </div>
            )}

            <div className="flex items-center justify-center space-x-2 text-gray-500 text-sm">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              <span>Please check back shortly</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-600 text-sm">
            Thank you for your patience
          </p>
        </div>
      </div>
    </div>
  );
};

